import { Path, useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../schema'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { IntegrationIcon } from '../../../../../../components/IntegrationIcon'
import { ReactComponent as ZocksIcon } from '../../../../../../assets/ZocksIcon.svg'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../common/integrations'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MentionData } from '../../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'
import { InputWithTokensElements } from '../../../../../../components/InputWithTokens/utils'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../hooks'
import { SelectDropdown, SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../../../../../components/Button/Button'
import { DescriptorType, DetailedCRMAccountIntegrationMapping, FieldDescriptor, FieldUpdateMode, OperationType } from '../../../../../../models/Account'
import { FieldMappingRow, OutboundFieldMappingRow } from './OutboundFieldMappingRow'
import { useGetSchemaErrors } from '../../schemaErrors'

interface OutboundFieldMappingProps {
  serviceId: IntegrationServiceId
  mappingKey: string
  isUpdatable?: boolean
  inputTokenOptions: MentionData[]
  onChange?: () => void
  getLabelForObject?: (objectName: string) => string
}

export const OutboundFieldMapping: React.FC<OutboundFieldMappingProps> = ({
  serviceId,
  mappingKey,
  isUpdatable,
  inputTokenOptions,
  onChange,
  getLabelForObject,
}) => {
  const { watch, setValue, getValues } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const { getErrorOnPath } = useGetSchemaErrors()

  const rootPath = `mappings.${mappingKey}` as const
  const fieldsPath = `mappings.${mappingKey}.fields` as Path<IntegrationSettings>
  const objectNamePath = `mappings.${mappingKey}.objectName` as Path<IntegrationSettings>
  const additionalInfosPath = `mappings.${mappingKey}.additionalInformation` as Path<IntegrationSettings>

  const rawFields = watch(fieldsPath)
  const additionalInformation = watch(additionalInfosPath) as { [key: string]: unknown } | undefined | null
  const objectName = watch(objectNamePath) as string
  const objectOperationType = watch(`mappings.${mappingKey}.operationType` as Path<IntegrationSettings>) as OperationType

  const mappedFields = useMemo(() => (rawFields ? rawFields : {}), [rawFields]) as { [key: string]: FieldDescriptor[] }
  const jsonMappedFields = JSON.stringify(Object.keys(mappedFields || {}))
  const objectIsUpdatable = useMemo(() => objectOperationType === OperationType.UPDATE, [objectOperationType])

  const objectLabel = getLabelForObject?.(objectName) ?? objectName

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: objectName! },
    },
    { skip: !objectName || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const fieldOptions: SelectOption[] = useMemo(() => {
    return currentObjectFormFieldDefinitions
      .filter((field) => !JSON.parse(jsonMappedFields).includes(field.name))
      .map((field) => ({
        id: field.name,
        label: `${field.label} (${field.name})`,
      }))
  }, [currentObjectFormFieldDefinitions, jsonMappedFields])

  const updateTitleSelectOptions = useMemo(() => {
    return currentObjectFormFieldDefinitions
      .filter((field) => JSON.parse(jsonMappedFields).includes(field.name))
      .map((field) => ({
        id: field.name,
        label: `${field.label} (${field.name})`,
      }))
  }, [currentObjectFormFieldDefinitions, jsonMappedFields])

  const initialUpdateTitleField: SelectOption | null = useMemo(() => {
    const savedTitle = (additionalInformation?.update as { title?: string })?.title ?? null
    return updateTitleSelectOptions.find((option) => option.id === savedTitle) || null
  }, [additionalInformation, updateTitleSelectOptions])

  const updateRootObject = useCallback(
    (updater: (current: DetailedCRMAccountIntegrationMapping) => DetailedCRMAccountIntegrationMapping) => {
      const current = getValues(rootPath as Path<IntegrationSettings>) as DetailedCRMAccountIntegrationMapping
      const updated = updater(current)
      setValue(rootPath as Path<IntegrationSettings>, updated, { shouldDirty: true, shouldValidate: true })
    },
    [getValues, setValue, rootPath],
  )

  useEffect(() => {
    if (isDescribeFetching) {
      return
    }
    if (!updateTitleSelectOptions.find((s) => s.id === initialUpdateTitleField?.id)) {
      updateRootObject((current) => {
        const currentAdditionalInformation = current.additionalInformation || {}
        const filteredAdditionalInformation = { ...currentAdditionalInformation }
        delete filteredAdditionalInformation?.update
        return {
          ...current,
          additionalInformation: filteredAdditionalInformation,
        }
      })
    }
  }, [initialUpdateTitleField, updateTitleSelectOptions, isDescribeFetching, updateRootObject])

  const handleUpdateTitleSelect = (selectedOption: SelectOption) => {
    updateRootObject((current) => {
      const currentAdditional = current.additionalInformation || {}
      return {
        ...current,
        additionalInformation: {
          ...currentAdditional,
          update: {
            ...(currentAdditional?.update as { title?: string }),
            title: selectedOption.id,
          },
        },
      }
    })
    onChange?.()
  }

  const [tempRows, setTempRows] = useState<FieldMappingRow[]>([])

  const handleAddTempRow = () => {
    setTempRows((prev) => [...prev, { id: crypto.randomUUID(), descriptors: [] }])
  }

  const handleRemoveTempRow = (id: string) => {
    setTempRows((prev) => prev.filter((row) => row.id !== id))
  }

  const handleRemoveSavedField = (key: string) => {
    updateRootObject((current) => {
      const newFields = { ...(current.fields || {}) }
      delete newFields[key]

      const newConstraints = { ...(current.constraints || {}) }
      delete newConstraints[key]

      return {
        ...current,
        fields: newFields,
        constraints: newConstraints,
      }
    })
    onChange?.()
  }

  const handleFieldSelect = useCallback(
    (selectedOption: SelectOption, tempRowId?: string, oldKey?: string) => {
      const newKey = selectedOption.id
      const newFormFieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === newKey)
      if (!newFormFieldDefinition) {
        return
      }

      updateRootObject((current) => {
        const newFields = { ...(current.fields || {}) }
        const newConstraints = { ...(current.constraints || {}) }

        if (oldKey && oldKey !== newKey) {
          const existingValue = newFields[oldKey]
          delete newFields[oldKey]
          newFields[newKey] = existingValue
        } else if (tempRowId) {
          const tempRow = tempRows.find((row) => row.id === tempRowId)
          if (tempRow) {
            newFields[newKey] = tempRow.descriptors
            handleRemoveTempRow(tempRowId)
          }
        }

        newConstraints[newKey] = {
          valueType: newFormFieldDefinition.type,
          valueFormat: newFormFieldDefinition.extraTypeInfo,
          maxLength: newFormFieldDefinition.maxLength,
        }

        if (oldKey) {
          delete newConstraints[oldKey]
        }

        return {
          ...current,
          fields: newFields,
          constraints: newConstraints,
        }
      })
      onChange?.()
    },
    [currentObjectFormFieldDefinitions, tempRows, updateRootObject, onChange],
  )

  const getRowsUpdateMode = useCallback(
    (rowId: string) => {
      const descriptors = mappedFields[rowId] as FieldDescriptor[] | undefined
      if (!descriptors) {
        return null
      }
      const match = descriptors.find((desc) => desc.updateMode !== null && desc.updateMode !== undefined)
      return match?.updateMode ?? null
    },
    [mappedFields],
  )

  const onInputWithTokensChange = useCallback(
    (elements: InputWithTokensElements[], rowId: string): FieldDescriptor[] => {
      const updateMode = getRowsUpdateMode(rowId)

      return elements.map((content) => {
        if (content.type === 'mention' && content.mentionData) {
          const mentionData: MentionData = content.mentionData
          const metadata = mentionData.metadata as { valueType?: string; valueFormat?: string; descriptorType?: string } | undefined
          return {
            type: metadata?.descriptorType === 'field' ? DescriptorType.FIELD : DescriptorType.VARIABLE,
            value: mentionData.id,
            valueType: metadata?.valueType ?? null,
            valueFormat: metadata?.valueFormat ?? null,
            updateMode: updateMode as FieldUpdateMode | null,
          }
        } else {
          return {
            type: DescriptorType.STATIC,
            value: content.content,
            updateMode: updateMode as FieldUpdateMode | null,
          }
        }
      })
    },
    [getRowsUpdateMode],
  )

  const onTempInputWithTokensChange = useCallback(
    (elements: InputWithTokensElements[], tempRowId: string) => {
      const newDescriptors = onInputWithTokensChange(elements, tempRowId)
      setTempRows((prevRows) => prevRows.map((row) => (row.id === tempRowId ? { ...row, descriptors: newDescriptors } : row)))
    },
    [onInputWithTokensChange],
  )

  const onFormInputWithTokensChange = useCallback(
    (elements: InputWithTokensElements[], formKey: string) => {
      const newDescriptors = onInputWithTokensChange(elements, formKey)
      updateRootObject((current) => {
        return {
          ...current,
          fields: {
            ...(current.fields || {}),
            [formKey]: newDescriptors,
          },
        }
      })
      onChange?.()
    },
    [onInputWithTokensChange, updateRootObject, onChange],
  )

  const mapFieldDescriptorToInputWithTokensElements = (fieldDescriptors: FieldDescriptor[]): InputWithTokensElements[] => {
    if (!fieldDescriptors) {
      return []
    }
    return fieldDescriptors.map((descriptor) => {
      const { type, value } = descriptor
      if (type === 'static') {
        return { type: 'text', content: value } as InputWithTokensElements
      }
      const inputTokenOption = inputTokenOptions.find((field) => field.id === value)
      if (!inputTokenOption) {
        return { type: 'text', content: value } as InputWithTokensElements
      }
      return {
        type: 'mention',
        content: `@${value}`,
        mentionData: { id: value, label: `${inputTokenOption.label}`, metadata: { ...inputTokenOption } },
      } as InputWithTokensElements
    })
  }

  if (currentObjectFormFieldDefinitions.length === 0 || isDescribeFetching) {
    return <Spinner />
  }

  const onChangeUpdatable = (updatable: boolean) => {
    updateRootObject((current) => ({
      ...current,
      operationType: updatable ? OperationType.UPDATE : OperationType.CREATE,
    }))
    onChange?.()
  }

  const onUpdateModeChange = (updateMode: FieldUpdateMode, rowId: string) => {
    updateRootObject((current) => {
      const existingDescriptors = (current.fields?.[rowId] as FieldDescriptor[]) || []
      const newDescriptors = existingDescriptors.map((descriptor) => ({
        ...descriptor,
        updateMode,
      }))
      return {
        ...current,
        fields: {
          ...(current.fields || {}),
          [rowId]: newDescriptors,
        },
      }
    })
    onChange?.()
  }

  const mappingError = getErrorOnPath(fieldsPath)
  const missingTitleError = getErrorOnPath(`${additionalInfosPath}.update.title`)

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4>Map {objectLabel} fields</h4>
          <p className='zocks-text-gray-600'>
            Match Zocks fields to {getIntegrationDisplayName(serviceId)} {objectName} fields.
          </p>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          {isUpdatable && (
            <Form.Check
              key={objectOperationType}
              type='checkbox'
              id={objectOperationType}
              label={`Update Meeting Summary fields in existing ${objectName} records`}
              checked={objectIsUpdatable}
              onChange={(e) => onChangeUpdatable(e.target.checked)}
            />
          )}
        </Col>
      </Row>

      <Row className='mb-2 fw-bold'>
        <Col className='d-flex align-items-center'>
          <ZocksIcon width='1rem' height='1rem' />
          <span className='ms-2'>ZOCKS FIELD</span>
        </Col>
        <Col className='d-flex align-items-center'>
          <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
          <span className='ms-2'>{getIntegrationDisplayName(serviceId).toUpperCase()} FIELD</span>
        </Col>
      </Row>
      {mappingError && <div className='invalid-feedback d-block'>{mappingError}</div>}

      {Object.entries(mappedFields).map(([key, descriptors]) => {
        const elements = mapFieldDescriptorToInputWithTokensElements(descriptors as FieldDescriptor[])
        const fieldError = getErrorOnPath(fieldsPath, key)
        const updateMode = descriptors.find((desc) => desc.updateMode !== null && desc.updateMode !== undefined)?.updateMode as FieldUpdateMode | undefined
        const existingDescriptors = mappedFields[key] || []
        const updateModeChangeDisabled = !existingDescriptors.length
        const currentFieldFormFieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === key) || { name: key, label: key }
        const currentFieldOptions = [
          ...fieldOptions,
          {
            id: currentFieldFormFieldDefinition.name,
            label: `${currentFieldFormFieldDefinition.label} (${currentFieldFormFieldDefinition.name})`,
          },
        ]
        const currentOption = currentFieldOptions.find((opt: SelectOption) => opt.id === key) || { id: key, label: key }
        return (
          <OutboundFieldMappingRow
            key={key}
            objectMappingRow={{ id: key, descriptors: descriptors as FieldDescriptor[] }}
            inputTokenOptions={inputTokenOptions}
            inputTokenElements={elements}
            onInputWithTokensChange={onFormInputWithTokensChange}
            externalFieldOptions={currentFieldOptions}
            initialExternalField={currentOption}
            handleExternalFieldSelect={(opt) => handleFieldSelect(opt, undefined, key)}
            isExternalFieldSelectLoading={isDescribeFetching}
            handleRemoveRow={handleRemoveSavedField}
            fieldError={fieldError}
            objectIsUpdatable={objectIsUpdatable}
            initialFieldUpdateMode={updateMode}
            onUpdateModeChange={(updateMode) => onUpdateModeChange(updateMode, key)}
            updateModeChangeDisabled={updateModeChangeDisabled}
          />
        )
      })}

      {tempRows.map((tempRow) => {
        const elements = mapFieldDescriptorToInputWithTokensElements(tempRow.descriptors)
        const updateMode = tempRow.descriptors.find((desc) => desc.updateMode !== null && desc.updateMode !== undefined)?.updateMode as
          | FieldUpdateMode
          | undefined
        return (
          <OutboundFieldMappingRow
            key={tempRow.id}
            objectMappingRow={{ id: tempRow.id, descriptors: tempRow.descriptors as FieldDescriptor[] }}
            inputTokenOptions={inputTokenOptions}
            inputTokenElements={elements}
            onInputWithTokensChange={onTempInputWithTokensChange}
            externalFieldOptions={fieldOptions}
            initialExternalField={null}
            handleExternalFieldSelect={(opt) => handleFieldSelect(opt, tempRow.id)}
            isExternalFieldSelectLoading={isDescribeFetching}
            handleRemoveRow={handleRemoveTempRow}
            objectIsUpdatable={objectIsUpdatable}
            initialFieldUpdateMode={updateMode}
            onUpdateModeChange={(updateMode) => onUpdateModeChange(updateMode, tempRow.id)}
            updateModeChangeDisabled={true}
          />
        )
      })}

      <Row>
        <Col>
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddTempRow}>
            + Add field
          </Button>
        </Col>
      </Row>
      <Row className='mt-4'>
        {objectIsUpdatable && (
          <div className='w-50'>
            <span className='me-2 fw-bold'>Title field for updates:</span>
            <SelectDropdown
              searchable
              options={updateTitleSelectOptions}
              initialSelectedOption={initialUpdateTitleField}
              placeholder='Select title field'
              onOptionSelected={handleUpdateTitleSelect}
              loading={isDescribeFetching}
              className='mt-2'
            />
            {missingTitleError && <div className='invalid-feedback d-block'>{missingTitleError}</div>}
          </div>
        )}
      </Row>
    </Container>
  )
}
